import { ButtonHTMLAttributes, forwardRef, FunctionComponent, ReactNode, SVGProps } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/utils/helpers";

export type KitButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type KitButtonSize = "sm" | "lg";

type Icon = FunctionComponent<SVGProps<SVGSVGElement>>;

/*
 * Button (CreativeOS Library.lib.pen, Components / Buttons and actions, style B,
 * Emad 1 Oct 2026). Every button is a capsule. Primary is the one decision on a
 * surface, the solid accent. Secondary is control glass with no outline. Ghost
 * is text only in the accent, and its hover tint is a capsule. Danger is solid
 * and reserved for irreversible removal. 32px (sm) is the standard height; 44px
 * (lg) is for touch sheets and modal confirmations only. The press settles to
 * 97% on the snappy spring.
 */
const BASE =
  "relative inline-flex shrink-0 items-center justify-center rounded-pill font-medium whitespace-nowrap select-none " +
  "[transition:transform_var(--spring-snappy),background-color_160ms_ease,color_160ms_ease] " +
  "not-disabled:active:scale-[0.97] data-[pressed]:scale-[0.97] " +
  "disabled:cursor-not-allowed disabled:opacity-40 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40 " +
  // Pending keeps the button at full strength: only the ring replaces the label.
  "aria-busy:cursor-wait aria-busy:opacity-100";

const VARIANT_CLASSES: Record<KitButtonVariant, string> = {
  primary: "bg-co-accent-fill text-co-on-accent not-disabled:hover:bg-co-accent-fill-hover",
  secondary:
    "co-glass-control text-co-text not-disabled:hover:bg-[color-mix(in_srgb,var(--co-glass-fill),var(--co-text)_6%)]",
  ghost: "bg-transparent text-co-accent not-disabled:hover:bg-co-accent-dim",
  danger: "bg-co-danger text-co-on-danger not-disabled:hover:bg-co-danger-hover",
};

const SIZE_CLASSES: Record<KitButtonSize, string> = {
  sm: "h-8 gap-2 px-3.5 text-[13px]",
  lg: "h-11 gap-2 px-5 text-[14px]",
};

/** Icon-only buttons are circles of the same height. */
const ICON_ONLY_CLASSES: Record<KitButtonSize, string> = {
  sm: "w-8 px-0",
  lg: "w-11 px-0",
};

export const KIT_BUTTON_ICON_CLASS: Record<KitButtonSize, string> = {
  sm: "size-3.5 shrink-0",
  lg: "size-4 shrink-0",
};

/** The one button style, shared by KitButton and the react-aria form Button. */
export function kitButtonClass({
  variant = "primary",
  size = "sm",
  iconOnly = false,
}: {
  variant?: KitButtonVariant;
  size?: KitButtonSize;
  iconOnly?: boolean;
} = {}) {
  return cn(BASE, VARIANT_CLASSES[variant], SIZE_CLASSES[size], iconOnly && ICON_ONLY_CLASSES[size]);
}

/** The pending ring: 16px, three quarters drawn, in the label's ink. */
export const KitButtonSpinner = ({ className }: { className?: string }) => (
  <span
    aria-hidden
    className={cn(
      "size-4 shrink-0 animate-spin rounded-full border-[1.75px] border-current border-r-transparent",
      className,
    )}
  />
);

export interface KitButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "disabled"> {
  variant?: KitButtonVariant;
  /** sm 32 (default) or lg 44, for touch sheets and modal confirmations. */
  size?: KitButtonSize;
  /** Keeps the button's width and shows the ring in place of the label. */
  loading?: boolean;
  isDisabled?: boolean;
  iconLeft?: Icon;
  iconRight?: Icon;
  children?: ReactNode;
}

export const KitButton = forwardRef<HTMLButtonElement, KitButtonProps>(
  (
    {
      variant = "primary",
      size = "sm",
      loading,
      isDisabled,
      iconLeft: IconLeft,
      iconRight: IconRight,
      children,
      className,
      type = "button",
      ...props
    },
    ref,
  ) => {
    const disabled = isDisabled || loading;
    const hasLabel = children != null && children !== "";
    const iconOnly = !hasLabel && Boolean(IconLeft || IconRight);

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled}
        data-disabled={disabled ? "true" : undefined}
        aria-busy={loading || undefined}
        className={cn(kitButtonClass({ variant, size, iconOnly }), className)}
        {...props}
      >
        <span className={cn("inline-flex items-center justify-center gap-[inherit]", loading && "invisible")}>
          {IconLeft && <IconLeft aria-hidden className={KIT_BUTTON_ICON_CLASS[size]} />}
          {hasLabel ? <span>{children}</span> : null}
          {IconRight && <IconRight aria-hidden className={KIT_BUTTON_ICON_CLASS[size]} />}
        </span>
        {loading ? (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <KitButtonSpinner />
          </span>
        ) : null}
      </button>
    );
  },
);

KitButton.displayName = "KitButton";

export interface KitSplitButtonProps {
  /** The default action, on the left half. */
  children: ReactNode;
  onPress?: () => void;
  /** Opens the choice menu: the right half. Render the menu beside it. */
  onChoose?: () => void;
  chooseLabel: string;
  chooseExpanded?: boolean;
  isDisabled?: boolean;
  className?: string;
}

/**
 * Button / Primary · Split: the primary action with a choice beside it, one
 * capsule cut in two by a 1px on-accent line. The left half does the default;
 * the right half opens the menu (Accept, and the window to accept it for).
 */
export const KitSplitButton = forwardRef<HTMLButtonElement, KitSplitButtonProps>(
  ({ children, onPress, onChoose, chooseLabel, chooseExpanded, isDisabled, className }, chooseRef) => (
    <span className={cn("inline-flex h-8 shrink-0", isDisabled && "opacity-40", className)}>
      <button
        type="button"
        disabled={isDisabled}
        onClick={onPress}
        className="inline-flex h-8 items-center gap-2 rounded-s-pill bg-co-accent-fill ps-3.5 pe-3 text-[13px] font-medium text-co-on-accent [transition:transform_var(--spring-snappy),background-color_160ms_ease] not-disabled:hover:bg-co-accent-fill-hover not-disabled:active:scale-[0.97] disabled:cursor-not-allowed"
      >
        {children}
      </button>
      <button
        ref={chooseRef}
        type="button"
        disabled={isDisabled}
        onClick={onChoose}
        aria-label={chooseLabel}
        aria-haspopup="menu"
        aria-expanded={chooseExpanded}
        className="grid h-8 w-8 place-items-center rounded-e-pill border-s border-co-on-accent bg-co-accent-fill text-co-on-accent transition-colors not-disabled:hover:bg-co-accent-fill-hover disabled:cursor-not-allowed"
      >
        <ChevronDown aria-hidden className="size-3.5" />
      </button>
    </span>
  ),
);

KitSplitButton.displayName = "KitSplitButton";
